// @TODO: use a new method like EditorStateProxy.createEmpty
import { MediaTypes } from '~/constants';

export type Image = {
  url: string;
  width: number | null;
  height: number | null;
  altText?: string | null;
  source?: number | null;
  mediaType: string;
  sourceLink?: string;
  originalUrl?: string | null;
};

export type Gif = {
  url: string;
  stillGifUrl: string;
  mediaType: string;
  width: number | null;
  height: number | null;
};

export type Video = {
  uploadId: string | null;
  name: string;
  duration: number;
  durationMs: number;
  size: number;
  width: number;
  height: number;
  frameRate?: number;
  url: string;
  originalUrl: string;
  thumbnail: string;
  availableThumbnails: string[];
  thumbOffset: number;
  mediaType: string;
  wasEdited: boolean;
};

export type Link = {
  url: string;
  expandedUrl: string | null;
  title: string | null;
  description: string | null;
  thumbnail: Image | null;
  thumbnailHttps: string | null;
  availableThumbnails: string[] | null;
  wasEdited: boolean;
};

export type SourceLink = {
  url: string;
  availableImages: Image[];
};

export type Retweet = {
  text: string;
  tweetId: number;
  userId: string;
  userName: string;
  userDisplayName: string;
  tweetUrl: string;
  avatarUrl: string;
};

// Link Attachment factory
export const getNewLink = ({
  url,
  expandedUrl,
  title = null,
  description = null,
  thumbnail = null,
  thumbnailHttps = null,
  availableThumbnails = null,
  wasEdited = false,
}): Link => ({
  url,
  expandedUrl,
  title,
  description,
  thumbnail,
  thumbnailHttps,
  availableThumbnails,
  wasEdited,
});

// SourceLink Attachment factory (source url and its available images)
export const getNewSourceLink = (url): SourceLink => ({
  url,
  availableImages: [],
});

// Available images factory
export const getNewAvailableImage = (image, sourceLink): Image => ({
  url: image.url,
  mediaType: MediaTypes.IMAGE,
  sourceLink,
  width: image.width,
  height: image.height,
});

// Image factory
export const getNewImage = ({
  url,
  width = null,
  height = null,
  altText = null,
  source = null,
  originalUrl = null,
}): Image => ({
  url,
  mediaType: MediaTypes.IMAGE,
  width,
  height,
  altText,
  source,
  originalUrl,
});

// Gif factory
export const getNewGif = (
  gifUrl,
  gifStillUrl,
  width = null,
  height = null
): Gif => ({
  url: gifUrl,
  stillGifUrl: gifStillUrl,
  mediaType: MediaTypes.GIF,
  width,
  height,
});

// Video factory
export const getNewVideo = ({
  uploadId = null,
  name,
  duration,
  durationMs,
  size,
  width,
  height,
  url,
  originalUrl,
  thumbnail,
  availableThumbnails,
  thumbOffset,
}): Video => ({
  uploadId,
  name,
  duration,
  durationMs,
  size,
  width,
  height,
  url,
  originalUrl,
  thumbnail,
  availableThumbnails,
  thumbOffset,
  mediaType: MediaTypes.VIDEO,
  wasEdited: false,
});

// Retweet factory
export const getNewRetweet = ({
  text,
  tweetId,
  userId,
  userName,
  userDisplayName,
  tweetUrl,
  avatarUrl,
}): Retweet => ({
  text,
  tweetId,
  userId,
  userName,
  userDisplayName,
  tweetUrl,
  avatarUrl,
});

export const getNewInstagramFeedbackObj = ({
  message,
  composerId = 'instagram',
  code = undefined,
}) => ({ message, composerId, code });

export const getNewProfile = (data) => ({
  id: data.id,
  service: {
    name: data.serviceName,
    username: data.serviceUsername,
    formattedUsername: data.serviceFormattedUsername,
    isPinterestBusiness: data.isPinterestBusiness,
  },
  images: {
    avatar: data.imagesAvatar,
  },
  timezone: data.timezone,
  shouldBeAutoSelected: data.shouldBeAutoSelected,
  isSelected: false,
  isDisabled: data.isDisabled,
  disabledMessage: data.disabledMessage,
  subprofiles: data.subprofiles, // Data structure in getNewSubprofile()
  selectedSubprofileId: null,
  serviceType: data.serviceType,
  serviceId: data.serviceId,
  isContributor: data.isContributor,
  isManager: data.isManager,
  subprofilesOrignatedFromAPI: null,
  instagramDirectEnabled:
    data.instagramDirectEnabled && data.serviceName === 'instagram',
  canPostComment: data.canPostComment,
  isPinterestBusiness: data.isPinterestBusiness,
  canAssociateLocation: data.canAssociateLocation,
  hasPushNotifications: data.hasPushNotifications,
  canQueueMoreThreads: data.canQueueMoreThreads,
  profileHasPostingSchedule: data.profileHasPostingSchedule,
  displayName: data.displayName,
  serverUrl: data.serverUrl,
  // Profile-specific app state
  appState: {
    isSubprofileCreationPending: false,
  },
});

export const getNewProfileGroup = ({ id, name, profileIds }) => ({
  id,
  name,
  profileIds,
});

export const getNewUserData = (data) => ({
  id: data.id,
  features: data.features,
  s3UploadSignature: data.s3UploadSignature,
  uses24hTime: data.uses24hTime,
  profileGroups: data.profileGroups.map(getNewProfileGroup),
  weekStartsMonday: data.weekStartsMonday,
  shouldAlwaysSkipEmptyTextAlert: data.shouldAlwaysSkipEmptyTextAlert,
  profilesSchedulesSlots: data.profilesSchedulesSlots,
});

export const getNewSubprofile = ({ avatar, id, name, isShared }) => ({
  avatar,
  id,
  name,
  isShared,
});

export const getNewPreventsSavingObj = ({
  message,
  composerId = null,
  code = undefined,
}) => ({ message, composerId, code });
